import React, { useCallback, useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Loader2, RefreshCw, Save, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const PRICE_FIELDS = [
  { key: 'input', label: 'Input' },
  { key: 'output', label: 'Output' },
  { key: 'cache_read', label: 'Cache read' },
  { key: 'cache_write_5m', label: 'Cache write 5m' },
  { key: 'cache_write_1h', label: 'Cache write 1h' },
];

const SOURCES = [
  { value: 'all', label: 'All' },
  { value: 'platform', label: 'Platform AI' },
  { value: 'claude_code', label: 'Claude Code' },
];

const RANGES = [
  { value: 'daily', label: 'Daily', note: 'Last 30 days' },
  { value: 'weekly', label: 'Weekly', note: 'Last 12 weeks' },
  { value: 'monthly', label: 'Monthly', note: 'Last 12 months' },
];

const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: v > 0 && v < 0.01 ? 4 : 2 })}`;
};
const compact = (n) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);

const SOURCE_COLORS = { platform_cost: '#2563eb', claude_code_cost: '#d97706' };

const ClaudeCostTab = ({ primaryColor }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('all');
  const [claudeOnly, setClaudeOnly] = useState(true);
  const [range, setRange] = useState('daily');

  const [prices, setPrices] = useState({});
  const [showPrices, setShowPrices] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/ai-costs/summary', {
        params: {
          source,
          claude_only: claudeOnly,
          // Viewer's UTC offset in minutes, so "today" is their today.
          tz_offset_minutes: -new Date().getTimezoneOffset(),
        },
      });
      setSummary(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [source, claudeOnly]);

  const fetchPrices = useCallback(async () => {
    try {
      const res = await api.get('/admin/ai-costs/prices');
      setPrices(res.data.prices || {});
    } catch (error) {
      toast.error('Failed to load price table');
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  const savePrices = async () => {
    try {
      setSavingPrices(true);
      const res = await api.put('/admin/ai-costs/prices', { prices });
      setPrices(res.data.prices || {});
      toast.success('Prices saved');
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save prices');
    } finally {
      setSavingPrices(false);
    }
  };

  const setPrice = (model, field, value) => {
    setPrices(prev => ({ ...prev, [model]: { ...prev[model], [field]: value === '' ? 0 : parseFloat(value) } }));
  };

  const addPriceRow = (model) => {
    const name = (model || '').trim();
    if (!name || prices[name]) return;
    setPrices(prev => ({ ...prev, [name]: { input: 0, output: 0, cache_read: 0, cache_write_5m: 0, cache_write_1h: 0 } }));
    setShowPrices(true);
  };

  const cards = summary ? [
    { label: 'Today', data: summary.totals.today },
    { label: 'Last 7 days', data: summary.totals.last_7_days },
    { label: 'Last 30 days', data: summary.totals.last_30_days },
    { label: 'Month to date', data: summary.totals.month_to_date },
  ] : [];

  const series = summary ? summary[range] : [];
  const rangeMeta = RANGES.find(r => r.value === range);
  const tickLabel = (label) => (range === 'weekly' ? label.replace('Week of ', '') : range === 'daily' ? label.slice(5) : label);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Source">
            {SOURCES.map(s => (
              <button
                key={s.value}
                onClick={() => setSource(s.value)}
                className={`px-3 py-1.5 text-sm ${source === s.value ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                style={source === s.value ? { backgroundColor: primaryColor } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>
          {source !== 'claude_code' && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={claudeOnly} onChange={e => setClaudeOnly(e.target.checked)} />
              Claude models only
            </label>
          )}
        </div>
        <button
          onClick={fetchSummary}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {!summary ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map(c => (
              <div key={c.label} className="bg-white border rounded-xl p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">{c.label}</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">{money(c.data.cost)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {compact(c.data.tokens)} tokens · {c.data.calls.toLocaleString()} calls
                </p>
                {c.data.cache_read > 0 && (
                  <p className="text-xs text-gray-400" title="Context re-read from cache on each call. Billed at about a tenth of the input rate.">
                    + {compact(c.data.cache_read)} cached reads
                  </p>
                )}
                {c.data.unpriced_calls > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{c.data.unpriced_calls} unpriced</p>
                )}
              </div>
            ))}
          </div>

          {/* Series */}
          <div className="bg-white border rounded-xl p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Cost over time</h3>
                <p className="text-xs text-gray-500">{rangeMeta.note} · all-time {money(summary.totals.all_time.cost)} · {compact(summary.totals.all_time.tokens)} tokens (+ {compact(summary.totals.all_time.cache_read)} cached reads){summary.tracking_since ? ` since ${summary.tracking_since}` : ''}</p>
              </div>
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Range">
                {RANGES.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setRange(r.value)}
                    className={`px-3 py-1.5 text-sm ${range === r.value ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    style={range === r.value ? { backgroundColor: primaryColor } : {}}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="label" tickFormatter={tickLabel} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} width={48} />
                  <Tooltip
                    formatter={(v, name) => [money(v), name === 'platform_cost' ? 'Platform AI' : 'Claude Code']}
                    labelFormatter={(l) => l}
                  />
                  <Bar dataKey="platform_cost" stackId="c" fill={SOURCE_COLORS.platform_cost} name="platform_cost" />
                  <Bar dataKey="claude_code_cost" stackId="c" fill={SOURCE_COLORS.claude_code_cost} name="claude_code_cost" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 mt-2 text-xs text-gray-600">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SOURCE_COLORS.platform_cost }} />Platform AI</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SOURCE_COLORS.claude_code_cost }} />Claude Code</span>
            </div>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-4 font-medium">{rangeMeta.label === 'Daily' ? 'Day' : rangeMeta.label === 'Weekly' ? 'Week' : 'Month'}</th>
                    <th className="py-2 pr-4 font-medium text-right">Cost</th>
                    <th className="py-2 pr-4 font-medium text-right" title="Input + output + cache writes">Tokens</th>
                    <th className="py-2 pr-4 font-medium text-right" title="Context re-read from cache; billed at about a tenth of the input rate">Cached reads</th>
                    <th className="py-2 font-medium text-right">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {[...series].reverse().map(row => (
                    <tr key={row.label} className="border-b last:border-0">
                      <td className="py-1.5 pr-4 text-gray-700">{row.label.replace('Week of ', '')}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">{money(row.cost)}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums text-gray-600">{compact(row.tokens)}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums text-gray-400">{compact(row.cache_read)}</td>
                      <td className="py-1.5 text-right tabular-nums text-gray-600">{row.calls.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Breakdowns */}
          <div className="grid lg:grid-cols-2 gap-4">
            {[['By model', summary.by_model], ['By feature', summary.by_feature]].map(([title, rows]) => (
              <div key={title} className="bg-white border rounded-xl p-4">
                <h3 className="font-medium text-gray-900 mb-3">{title} <span className="text-xs font-normal text-gray-500">(all time)</span></h3>
                {rows.length === 0 ? (
                  <p className="text-sm text-gray-500">No usage recorded yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.name} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 text-gray-700 break-all">{r.name}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">
                            {money(r.cost)}
                            {r.unpriced_calls > 0 && <span className="text-amber-600" title={`${r.unpriced_calls} calls have no price`}> *</span>}
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-gray-500" title={`${compact(r.cache_read)} cached reads`}>{compact(r.tokens)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>

          {/* Unpriced models */}
          {summary.unpriced_models.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-medium">Some usage has no price, so it isn&apos;t in the cost totals (tokens still count).</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {summary.unpriced_models.map(m => (
                  <button key={m} onClick={() => addPriceRow(m)} className="flex items-center gap-1 px-2 py-1 bg-white border border-amber-300 rounded-lg hover:bg-amber-100">
                    <Plus className="w-3 h-3" /> Set price for {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Price table */}
          <div className="bg-white border rounded-xl">
            <button
              onClick={() => setShowPrices(s => !s)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <span>
                <span className="font-medium text-gray-900">Price table</span>
                <span className="text-xs text-gray-500 ml-2">USD per 1M tokens, used to estimate cost</span>
              </span>
              {showPrices ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>
            {showPrices && (
              <div className="px-4 pb-4 space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="py-2 pr-3 font-medium">Model</th>
                        {PRICE_FIELDS.map(f => <th key={f.key} className="py-2 pr-3 font-medium">{f.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(prices).map(([model, p]) => (
                        <tr key={model} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 text-gray-700 break-all">{model}</td>
                          {PRICE_FIELDS.map(f => (
                            <td key={f.key} className="py-1.5 pr-3">
                              <input
                                type="number" min="0" step="0.01"
                                value={p[f.key] ?? 0}
                                onChange={e => setPrice(model, f.key, e.target.value)}
                                className="w-20 px-2 py-1 border rounded"
                                aria-label={`${model} ${f.label} price`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <form
                    className="flex items-center gap-2"
                    onSubmit={e => { e.preventDefault(); addPriceRow(e.target.elements.newModel.value); e.target.reset(); }}
                  >
                    <input name="newModel" placeholder="Add model id, e.g. claude-opus-5-5" className="px-3 py-1.5 border rounded-lg text-sm w-64" />
                    <button type="submit" className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Add</button>
                  </form>
                  <button
                    onClick={savePrices}
                    disabled={savingPrices}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {savingPrices ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save prices
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Tokens are new tokens processed (input, output, cache writes). Long conversations re-read their whole context from cache on every call, so
            "cached reads" run into the tens of millions; they are billed at about a tenth of the input rate and are counted in the cost.
            Platform AI = calls the app makes (Intel agents, LOI analysis, company descriptions, Claude Local), logged from the day tracking was deployed.
            Claude Code = sessions in this project, read from its transcripts. Costs are list-price estimates (tokens × the price table, or the figure
            the Claude CLI reports); a Claude subscription is billed differently, so treat this as usage value, not an invoice. Days follow your browser&apos;s timezone.
          </p>
        </>
      )}
    </div>
  );
};

export default ClaudeCostTab;
